import React, {useState, useEffect, useMemo} from 'react';
import {Formik, Form, useField} from 'formik';
import * as yup from 'yup';

import {cx} from '../../../util';
import {
  TextInput, FormikDropdownInput, LabelAndErrors, StyledCheckbox, StyledLabel, InputWithSuggestions,
} from '../../../components/Forms';
import StepNavigation from '../StepNavigation';

import {TYPES_BY_ROLE_CATEGORY} from '../../../../../lib/constants/tags';
import {STAGES} from '../../../../../lib/utils';

const StageSelector = ({
  name, stageTypesName, onToggle, tags, roleCategory,
}) => {
  const [stagesField, , stagesHelpers] = useField(name);
  const [typesField, , typesHelpers] = useField(stageTypesName);
  const [interviewTypes, setInterviewTypes] = useState({});

  const suggestions = useMemo(() => {
    const typeTags = tags?.type || [];
    const allowedTypes = TYPES_BY_ROLE_CATEGORY[roleCategory] || [];
    // If we have a role category with allowed types, filter to those; otherwise show all
    if (allowedTypes.length > 0) {
      return typeTags
        .filter((tag) => allowedTypes.includes(tag.id))
        .map((tag) => ({item: {id: tag.id, name: tag.name}}));
    }
    return typeTags.map((tag) => ({item: {id: tag.id, name: tag.name}}));
  }, [tags, roleCategory]);

  const selectedStages = stagesField.value || [];
  const stageTypes = typesField.value || {};

  const toggleStage = (stageId) => {
    let newStages;
    const newTypes = {...stageTypes};

    if (selectedStages.includes(stageId)) {
      newStages = selectedStages.filter((s) => s !== stageId);
      delete newTypes[stageId];
    } else {
      newStages = [...selectedStages, stageId];
    }

    stagesHelpers.setValue(newStages);
    typesHelpers.setValue(newTypes);
    if (onToggle) onToggle({stagesCompleted: newStages, stageInterviewTypes: newTypes});
  };

  const getFilteredSuggestions = (stageId, query) => {
    const q = query.toLowerCase();
    return suggestions.filter(({item}) => item.name.toLowerCase().includes(q));
  };

  const updateSuggestionsForStage = (stageId) => (query) => {
    setInterviewTypes((prev) => ({
      ...prev,
      [stageId]: getFilteredSuggestions(stageId, query),
    }));
  };

  // Initialize suggestions for all stages
  useEffect(() => {
    const initial = {};
    STAGES.forEach(({id}) => {
      initial[id] = suggestions;
    });
    setInterviewTypes(initial);
  }, [suggestions]);

  return (
    <div className="flex flex-col gap-2">
      {STAGES.map(({id, label}) => {
        const isSelected = selectedStages.includes(id);
        return (
          <div
            key={id}
            className={cx(
              'border rounded-xl px-4 py-3 transition-colors',
              isSelected ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 bg-white',
            )}
          >
            <div className="flex items-center gap-3">
              <StyledCheckbox
                id={`stage-${id}`}
                checked={isSelected}
                onChange={() => toggleStage(id)}
              />
              <StyledLabel
                htmlFor={`stage-${id}`}
                className="flex-1 mb-0 font-normal"
              >
                {label}
              </StyledLabel>
            </div>
            {isSelected && (
              <div className="mt-3 ml-7">
                <InputWithSuggestions
                  name={`${stageTypesName}.${id}`}
                  placeholder="Add interview types"
                  suggestions={interviewTypes[id] || suggestions}
                  updateSuggestions={updateSuggestionsForStage(id)}
                  multiSelect
                  strictSelection
                  onBlur={() => onToggle?.({
                    stagesCompleted: selectedStages,
                    stageInterviewTypes: typesField.value,
                  })}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth(); // 0-indexed
const years = Array.from({length: 3}, (_, i) => String(currentYear - i));

export const monthOptions = months.map((m) => ({value: m, label: m}));
export const yearOptions = years.map((y) => ({value: y, label: y}));

export const dropdownOptionsStyle = {
  top: 46,
  width: 'unset',
  maxWidth: 300,
  maxHeight: 300,
};

const DURATION_UNITS = [
  {value: 'days', label: 'Days'},
  {value: 'weeks', label: 'Weeks'},
  {value: 'months', label: 'Months'},
];

export const askedOnSchemaFields = {
  askedOnMonth: yup.string().required('Required')
    .test(
      'not-in-future',
      'Interview can\'t be in the future',
      function notInFuture(month) {
        const {askedOnYear} = this.parent;
        const monthIndex = months.indexOf(month);
        if (!askedOnYear || monthIndex === -1) return true;
        return new Date(Number(askedOnYear), monthIndex) <= new Date(currentYear, currentMonth);
      },
    ),
  askedOnYear: yup.string().required('Required'),
};

const schema = yup.object({
  ...askedOnSchemaFields,
  durationValue: yup.number().positive().integer().nullable(),
  durationUnit: yup.string(),
  stagesCompleted: yup.array().of(yup.string()).min(1, 'Please select at least one stage'),
  stageInterviewTypes: yup.object(),
});

const stagesToFormValues = (stages) => {
  const stagesCompleted = (stages || []).map((s) => s.name);
  const stageInterviewTypes = {};
  (stages || []).forEach((s) => {
    if (s.interviewTypes?.length) {
      stageInterviewTypes[s.name] = s.interviewTypes;
    }
  });
  return {stagesCompleted, stageInterviewTypes};
};

const formValuesToStages = (stagesCompleted, stageInterviewTypes, existingStages) => (
  (stagesCompleted || []).map((name) => ({
    name,
    interviewTypes: stageInterviewTypes?.[name] || [],
    questions: existingStages?.find((s) => s.name === name)?.questions || [],
  }))
);

const ProcessStep = ({
  draft, onNext, onBack, onEdit, tags,
}) => {
  // Determine roleCategory from draft.role
  const roleCategory = useMemo(() => {
    if (!draft.role || !tags?.role) return '';
    const matchingRole = tags.role.find((r) => r.id === draft.role);
    return matchingRole?.parent_tag || '';
  }, [draft.role, tags?.role]);

  const {stagesCompleted, stageInterviewTypes} = useMemo(
    () => stagesToFormValues(draft.stages),
    [draft.stages],
  );

  // Hack to keep initialValues stable across renders,
  // preventing enableReinitialize from resetting form values
  const [initialValues] = useState(() => ({
    askedOnMonth: draft.askedOnMonth || '',
    askedOnYear: draft.askedOnYear || '',
    durationValue: draft.durationValue || '',
    durationUnit: draft.durationUnit || 'weeks',
    stagesCompleted,
    stageInterviewTypes,
  }));

  const handleToggle = ({stagesCompleted: sc, stageInterviewTypes: sit}) => {
    onEdit?.({stages: formValuesToStages(sc, sit, draft.stages)});
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => onNext({
        ...values,
        stages: formValuesToStages(
          values.stagesCompleted, values.stageInterviewTypes, draft.stages,
        ),
      })}
      enableReinitialize
    >
      {({isSubmitting}) => (
        <Form className="flex flex-col gap-y-1">
          <LabelAndErrors
            name="askedOnMonth"
            label="When was your most recent interview?"
            required
            suppressErrorMessage
          >
            <div className="grid grid-cols-2 gap-x-2 -mb-4">
              <FormikDropdownInput
                name="askedOnMonth"
                placeholder="Month"
                options={monthOptions}
                onSelect={(v) => onEdit?.({askedOnMonth: v})}
                optionsStyles={dropdownOptionsStyle}
              />
              <FormikDropdownInput
                name="askedOnYear"
                placeholder="Year"
                options={yearOptions}
                onSelect={(v) => onEdit?.({askedOnYear: v})}
                optionsStyles={dropdownOptionsStyle}
              />
            </div>
          </LabelAndErrors>

          {draft.result !== 'in_progress' && (
            <LabelAndErrors name="durationValue" label="How long was the interview process?">
              <div className="grid grid-cols-2 gap-x-2 -mb-4">
                <TextInput
                  name="durationValue"
                  inputMode="numeric"
                  placeholder="e.g. 2"
                  autoComplete="off"
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  }}
                  onBlur={(e) => onEdit?.({durationValue: e.target.value})}
                />
                <FormikDropdownInput
                  name="durationUnit"
                  options={DURATION_UNITS}
                  onSelect={(v) => onEdit?.({durationUnit: v})}
                  optionsStyles={dropdownOptionsStyle}
                />
              </div>
            </LabelAndErrors>
          )}

          <LabelAndErrors name="stagesCompleted" label="What interview stages did you complete?" required>
            <StageSelector
              name="stagesCompleted"
              stageTypesName="stageInterviewTypes"
              onToggle={handleToggle}
              tags={tags}
              roleCategory={roleCategory}
            />
          </LabelAndErrors>

          <StepNavigation onBack={onBack} isSubmitting={isSubmitting} />
        </Form>
      )}
    </Formik>
  );
};

export default ProcessStep;
